import { useRef, useState } from "react";
import { Card } from "../../CommonElements/Hajj/Card";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../CommonElements/PageTitle";
import { Button, InputGroup, Stack } from "react-bootstrap";
import { Container, Form, Label, Spinner } from "reactstrap";
import { AntInput } from "../../CommonElements/AntInput";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CircleLockCheck01Icon,
  SquareLock01Icon,
  ViewIcon,
  ViewOffSlashIcon,
} from "@hugeicons/core-free-icons";
import { useChangePasswordMutation } from "../../services/user";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../_helper/Auth";

export const ChangePassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    passwordChangeRequired,
    setPasswordChangeRequired,
    emailVerificationPending,
    addUser,
    user,
  } = useAuthContext();
  /** When false after success, skip inline success UI (mandatory flow navigates away). */
  const showVoluntarySuccessRef = useRef(true);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
    reset,
  } = useForm({
    defaultValues: {
      current_password: "",
      new_password: "",
      new_password_confirmation: "",
    },
  });

  const {
    mutate: changePasswordMutation,
    isPending,
    isSuccess,
  } = useChangePasswordMutation(setError);

  const new_password = watch("new_password");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const onSubmit = (data) => {
    if (data.new_password !== data.new_password_confirmation) {
      setError("new_password_confirmation", {
        type: "manual",
        message: t("PASSWORDS_DO_NOT_MATCH"),
      });
      return;
    }

    showVoluntarySuccessRef.current = !passwordChangeRequired;

    changePasswordMutation(
      {
        current_password: data.current_password,
        new_password: data.new_password,
        new_password_confirmation: data.new_password_confirmation,
      },
      {
        onSuccess: (res) => {
          if (!showVoluntarySuccessRef.current) {
            setPasswordChangeRequired(false);
            const nextUser = res?.data?.user;
            if (nextUser) {
              addUser(nextUser);
            } else if (user) {
              addUser({ ...user, password_change_required: false });
            }
            navigate(
              emailVerificationPending ? "/verify-email" : "/app",
              { replace: true }
            );
            return;
          }
          reset();
        },
      }
    );
  };

  return (
    <Stack gap={5}>
      {!passwordChangeRequired && (
        <PageHeader title={t("CHANGE_PASSWORD")} backTo="/app/me" />
      )}

      <Container fluid>
        <Card
          className="pattern-card mx-auto"
          style={{ maxWidth: "36.3125rem" }}
        >
          {isSuccess && !showVoluntarySuccessRef.current ? (
            <div className="py-5 text-center">
              <Spinner />
            </div>
          ) : isSuccess && showVoluntarySuccessRef.current ? (
            <div className="py-5">
              <div className="mb-4 text-center">
                <HugeiconsIcon
                  color="green"
                  size={60}
                  icon={CircleLockCheck01Icon}
                />
              </div>
              <h2 className="text-success mb-3 text-center">
                {t("PASSWORD_CHANGED_SUCCESSFULLY")}
              </h2>
              <p className="text-secondary mb-4 fw-bold text-center">
                تم تغيير كلمة المرور بنجاح. يمكنك الآن استخدام كلمة المرور
                الجديدة لتسجيل الدخول.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <p className="h2">{t("CHANGE_PASSWORD")}</p>
                <p className="text-secondary text-medium fw-bold">
                  {t("FILL_THE_FIELDS_BELOW_TO_CHANGE_PASSWORD")}
                </p>
              </div>

              <Form onSubmit={handleSubmit(onSubmit)}>
                <InputGroup className="mb-3">
                  <Label
                    htmlFor="current-password"
                    className="fw-bold required"
                  >
                    {t("CURRENT_PASSWORD")}
                  </Label>
                  <Controller
                    name="current_password"
                    control={control}
                    rules={{
                      required: t("CURRENT_PASSWORD_REQUIRED"),
                    }}
                    render={({ field }) => (
                      <div className="position-relative w-100">
                        <AntInput
                          {...field}
                          id="current-password"
                          prefix={<HugeiconsIcon icon={SquareLock01Icon} />}
                          type={showCurrentPassword ? "text" : "password"}
                          placeholder={t("CURRENT_PASSWORD")}
                        />
                        <button
                          type="button"
                          className="show-hide"
                          aria-label={
                            showCurrentPassword
                              ? t("HIDE_PASSWORD")
                              : t("SHOW_PASSWORD")
                          }
                          aria-pressed={showCurrentPassword}
                          onClick={() => setShowCurrentPassword((prev) => !prev)}
                        >
                          {showCurrentPassword ? (
                            <HugeiconsIcon icon={ViewOffSlashIcon} />
                          ) : (
                            <HugeiconsIcon icon={ViewIcon} />
                          )}
                        </button>
                      </div>
                    )}
                  />
                  {errors.current_password && (
                    <div className="text-danger small mt-1">
                      {errors.current_password.message}
                    </div>
                  )}
                </InputGroup>

                <InputGroup className="mb-3">
                  <Label htmlFor="new-password" className="fw-bold required">
                    {t("NEW_PASSWORD")}
                  </Label>
                  <Controller
                    name="new_password"
                    control={control}
                    rules={{
                      required: t("NEW_PASSWORD_REQUIRED"),
                      minLength: {
                        value: 8,
                        message: t("PASSWORD_MIN_LENGTH"),
                      },
                    }}
                    render={({ field }) => (
                      <div className="position-relative w-100">
                        <AntInput
                          {...field}
                          id="new-password"
                          prefix={<HugeiconsIcon icon={SquareLock01Icon} />}
                          type={showNewPassword ? "text" : "password"}
                          placeholder={t("NEW_PASSWORD")}
                        />
                        <button
                          type="button"
                          className="show-hide"
                          aria-label={
                            showNewPassword
                              ? t("HIDE_PASSWORD")
                              : t("SHOW_PASSWORD")
                          }
                          aria-pressed={showNewPassword}
                          onClick={() => setShowNewPassword((prev) => !prev)}
                        >
                          {showNewPassword ? (
                            <HugeiconsIcon icon={ViewOffSlashIcon} />
                          ) : (
                            <HugeiconsIcon icon={ViewIcon} />
                          )}
                        </button>
                      </div>
                    )}
                  />
                  {errors.new_password && (
                    <div className="text-danger small mt-1">
                      {errors.new_password.message}
                    </div>
                  )}
                </InputGroup>

                <InputGroup className="mb-3">
                  <Label
                    htmlFor="password-confirmation"
                    className="fw-bold required"
                  >
                    {t("NEW_PASSWORD_CONFIRMATION")}
                  </Label>
                  <Controller
                    name="new_password_confirmation"
                    control={control}
                    rules={{
                      required: t("NEW_PASSWORD_CONFIRMATION_REQUIRED"),
                      validate: (value) =>
                        value === new_password || t("PASSWORDS_DO_NOT_MATCH"),
                    }}
                    render={({ field }) => (
                      <div className="position-relative w-100">
                        <AntInput
                          {...field}
                          id="password-confirmation"
                          prefix={<HugeiconsIcon icon={SquareLock01Icon} />}
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder={t("NEW_PASSWORD_CONFIRMATION")}
                        />
                        <button
                          type="button"
                          className="show-hide"
                          aria-label={
                            showConfirmPassword
                              ? t("HIDE_PASSWORD")
                              : t("SHOW_PASSWORD")
                          }
                          aria-pressed={showConfirmPassword}
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                        >
                          {showConfirmPassword ? (
                            <HugeiconsIcon icon={ViewOffSlashIcon} />
                          ) : (
                            <HugeiconsIcon icon={ViewIcon} />
                          )}
                        </button>
                      </div>
                    )}
                  />
                  {errors.new_password_confirmation && (
                    <div className="text-danger small mt-1">
                      {errors.new_password_confirmation.message}
                    </div>
                  )}
                </InputGroup>

                <Button
                  type="submit"
                  disabled={isPending}
                  variant="primary"
                  className="w-100"
                >
                  {isPending ? (
                    <>
                      <Spinner size="sm" />
                      {t("SENDING")}
                    </>
                  ) : (
                    t("SEND")
                  )}
                </Button>
              </Form>
            </>
          )}
        </Card>
      </Container>
    </Stack>
  );
};
